/* eslint-disable @typescript-eslint/ban-types */
/**
 *  ===============================================================================================
 *     ____ _____ _   _          _______     __                 _____           _     _    _
 *    |  _ \_   _| \ | |   /\   |  __ \ \   / /                / ____|         | |   | |  | |
 *    | |_) || | |  \| |  /  \  | |__) \ \_/ /__  _ __   ___  | |  __ _ __ ___ | |__ | |__| |
 *    |  _ < | | | . ` | / /\ \ |  _  / \   / _ \| '_ \ / _ \ | | |_ | '_ ` _ \| '_ \|  __  |
 *    | |_) || |_| |\  |/ ____ \| | \ \  | | (_) | | | |  __/ | |__| | | | | | | |_) | |  | |
 *    |____/_____|_| \_/_/    \_\_|  \_\ |_|\___/|_| |_|\___|  \_____|_| |_| |_|_.__/|_|  |_|
 *
 *  ===============================================================================================
 * @author    BINARY one GmbH
 * @license Unlicensed
 */

import "/src/scss/styles.scss";
import "@elastic/react-search-ui-views/lib/styles/styles.css";

//in your `main.js` file
import { BASEURL, SITEURL, ISLOGGEDIN } from "@libTs/cms.globals";

import { createApp, ref } from "vue";

// Component imports
import NewsOverview from "./components/news-overview.vue";
import ConsPreview from "./components/consultants-preview.vue";
import FooterMap from "./components/map.vue";
import RegisterForm from "./components/register-form.vue";
import ResetPasswordForm from "./components/reset-password-form.vue";
import SupplierSourcingForm from "./components/supplier-sourcing-form.vue";
import UnitConverter from "./components/tools/unit-converter.vue";
import MolarMass from "./components/tools/molar-mass.vue";
import PeriodicTable from "./components/tools/periodic-table.vue";
import PpmToMgl from "./components/tools/ppm-to-mgl.vue";
import SetPasswordForm from "./components/set-password-form.vue";
import { GoogleMap, AdvancedMarker, InfoWindow } from "vue3-google-map";
import LoginForm from "./components/login-form.vue";
import CsvUpload from "./components/csv-upload.vue";
import ThpEdit from "./components/thp-edit.vue";
import Cart from "./components/cart.vue";
import Checkout from "./components/checkout.vue";
import OfferCheckout from "./components/offer-checkout.vue";
import CartOverview from "./components/cart-overview.vue";
import Orders from "./components/orders.vue";
import Catalog from "./components/oci/catalog.vue";
import ProductLists from "./components/product-lists.vue";
import QuickOrder from "./components/quick-order.vue";
import ExcelUpload from "./components/excel-upload.vue";
import HomeSlider from "./components/home-slider.vue";
import BrandsOverview from "./components/brands-overview.vue";
import BrandsDownloads from "./components/brands-downloads.vue";
import NewsPage from "./components/news-page.vue";
import TeamOverview from "./components/team-overview.vue";
import Accordion from "./components/accordion.vue";
import Infographic from "./components/infographic.vue";
import ProductImages from "./components/product-images.vue";
import ProductAccordion from "./components/product-accordion.vue";
import ProductButtons from "./components/product-buttons.vue";
import ProductSlider from "./components/product-slider.vue";
import comparisonPage from "./components/comparison-page.vue";

import SearchSection from "./components/search/search-section.vue";
import SearchSectionBrand from "./components/search/search-section-brand.vue";
import SearchSectionCategory from "./components/search/search-section-category.vue";

import SearchHomeHeader from "./components/search/search-home-header.vue";

import watchlist from "./components/watchlist.vue";
import compareList from "./components/compare-list.vue";
import cartList from "./components/cart-list.vue";
import offerList from "./components/offer-list.vue";

import toolsSlideup from "./components/tools/tools-slideup.vue";

import scrollToTop from "./components/scroll-to-top.vue";

// Helper imports
import { ItemHelper } from "./helpers/item.helper";
import { InfographicHelper } from "./helpers/infographic.helper";
import { SliderHelper } from "./helpers/slider.helper";
import { vDropdown } from "./directives/v-dropdown";
import { vLoad } from "./directives/v-load";
import { vInputText } from "./directives/v-input-text";
import { vSmiles } from "./directives/v-smiles";
import { vClickOutside } from "./directives/v-click-outside";

// Service imports
import { OverlaysService } from "./services/overlays.service";
import { TranslationService } from "./services/translation.service";
import { ItemService } from "./services/item.service";
import { ProductsService } from "./services/products.service";
import { ToolsService } from "./services/tools.service";

// Helper imports
import { CartService } from "./services/cart.service";
import { WatchlistService } from "./services/watchlist.service";
import { CompareService } from "./services/compare.service";
import { OfferService } from "./services/offer.service";

// -----------------------------------------------------------
// DEBUG output
const ISDEBUG = import.meta.env.DEV;

if (ISDEBUG) {
    console.group("Environment loaded");
    console.log(ISDEBUG);
    console.log(
        " -----------------------------------\n",
        ` BASEURL:\t\t${BASEURL}\n`,
        ` SITEURL:\t\t${SITEURL}\n`,
        "-----------------------------------",
    );
    console.groupEnd();
}

const overlaysService = new OverlaysService();

const app = createApp({
    setup() {},
    data() {
        return {
            overlaysSvc: overlaysService,
            translationSvc: new TranslationService(),
            toolsSvc: new ToolsService(),
            brands: {
                letter: ref(""),
            },
            tabs: {
                current: ref(0),
            },
            ItemHelper: new ItemHelper(),
            InfographicHelper: new InfographicHelper(),
            cartSvc: new CartService(overlaysService, ISLOGGEDIN),
            watchlistSvc: new WatchlistService(overlaysService, ISLOGGEDIN),
            compareSvc: new CompareService(overlaysService, ISLOGGEDIN),
            offerSvc: new OfferService(overlaysService, ISLOGGEDIN),
            itemSvc: new ItemService(),
            productsSvc: new ProductsService(),
        };
    },

    provide() {
        return {
            overlaysSvc: this.overlaysSvc,
            translationSvc: this.translationSvc,
            toolsSvc: this.toolsSvc,
            productsSvc: this.productsSvc,
            cartSvc: this.cartSvc,
            watchlistSvc: this.watchlistSvc,
            compareSvc: this.compareSvc,
            offerSvc: this.offerSvc,
            ItemHelper: this.ItemHelper,
            InfographicHelper: this.InfographicHelper,
            itemSvc: this.itemSvc,
        };
    },
    components: {
        NewsOverview,
        ConsPreview,
        FooterMap,
        GoogleMap,
        AdvancedMarker,
        InfoWindow,
        LoginForm,
        RegisterForm,
        ResetPasswordForm,
        SupplierSourcingForm,
        UnitConverter,
        MolarMass,
        PeriodicTable,
        PpmToMgl,
        SetPasswordForm,
        Checkout,
        OfferCheckout,
        CartOverview,
        CsvUpload,
        ThpEdit,
        Orders,
        Catalog,
        Cart,
        ProductLists,
        QuickOrder,
        ExcelUpload,
        HomeSlider,
        BrandsOverview,
        BrandsDownloads,
        NewsPage,
        TeamOverview,
        Accordion,
        Infographic,
        SearchSection,
        SearchSectionBrand,
        SearchSectionCategory,
        SearchHomeHeader,
        watchlist,
        compareList,
        cartList,
        offerList,
        ProductImages,
        ProductAccordion,
        ProductButtons,
        ProductSlider,
        toolsSlideup,
        comparisonPage,
        scrollToTop,
    },
    directives: {
        Dropdown: vDropdown,
        InputText: vInputText,
        Load: vLoad,
        Smiles: vSmiles,
        ClickOutside: vClickOutside,
    },
});

app.mount("#app");

// Init sliders
const sliderHelper = new SliderHelper(".slider");

sliderHelper.initSliders();
